import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
export function BillApprovalWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [approvalDecision, setApprovalDecision] = useState<'approve' | 'reject' | null>(null);
  const [notes, setNotes] = useState('');
  const [verificationChecks, setVerificationChecks] = useState({
    supplierVerified: false,
    amountVerified: false,
    itemsVerified: false,
    termsVerified: false
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-001',
    supplierName: 'Premium Tea Estates Ltd',
    amount: 125000,
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [{
      id: '1',
      description: 'Ceylon Black Tea - Grade A',
      quantity: 500,
      unitPrice: 200,
      total: 100000
    }, {
      id: '2',
      description: 'Packaging & Handling',
      quantity: 1,
      unitPrice: 25000,
      total: 25000
    }]
  };
  const handleCheckboxChange = (key: keyof typeof verificationChecks) => {
    setVerificationChecks({
      ...verificationChecks,
      [key]: !verificationChecks[key]
    });
  };
  const allChecksCompleted = Object.values(verificationChecks).every(check => check);
  const handleSubmit = () => {
    console.log('Submitting approval:', {
      billId: id,
      decision: approvalDecision,
      notes,
      verificationChecks
    });
    navigate('/bills');
  };
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3937">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3938">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3939">
          <ArrowLeftIcon size={20} data-id="element-3940" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3941">
        <div className="flex items-center justify-between mb-8" data-id="element-3942">
          <div className="flex-1" data-id="element-3943">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3944">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3945">
                1
              </div>
              <div className="ml-3" data-id="element-3946">
                <div className="text-sm font-medium" data-id="element-3947">Review Bill</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3948"></div>
          <div className="flex-1" data-id="element-3949">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3950">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3951">
                2
              </div>
              <div className="ml-3" data-id="element-3952">
                <div className="text-sm font-medium" data-id="element-3953">Verification</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3954"></div>
          <div className="flex-1" data-id="element-3955">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3956">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3957">
                3
              </div>
              <div className="ml-3" data-id="element-3958">
                <div className="text-sm font-medium" data-id="element-3959">Decision</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Review Bill */}
        {step === 1 && <div className="space-y-6" data-id="element-3960">
            <div data-id="element-3961">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3962">
                Review Bill Details
              </h2>
              <p className="text-gray-600" data-id="element-3963">
                Please review the bill information carefully before proceeding
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3964">
              <div className="grid grid-cols-2 gap-4" data-id="element-3965">
                <div data-id="element-3966">
                  <div className="text-sm text-gray-500" data-id="element-3967">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3968">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-3969">
                  <div className="text-sm text-gray-500" data-id="element-3970">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3971">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-3972">
                  <div className="text-sm text-gray-500" data-id="element-3973">Issue Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3974">
                    {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-3975">
                  <div className="text-sm text-gray-500" data-id="element-3976">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3977">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
              <div data-id="element-3978">
                <div className="text-sm text-gray-500 mb-2" data-id="element-3979">Description</div>
                <div className="text-base text-gray-900" data-id="element-3980">
                  {bill.description}
                </div>
              </div>
              <div data-id="element-3981">
                <div className="text-sm text-gray-500 mb-3" data-id="element-3982">Items</div>
                <div className="space-y-2" data-id="element-3983">
                  {bill.items.map(item => <div key={item.id} className="flex justify-between items-center p-3 bg-white rounded border border-gray-200" data-id="element-3984">
                      <div data-id="element-3985">
                        <div className="text-sm font-medium text-gray-900" data-id="element-3986">
                          {item.description}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3987">
                          {item.quantity} × Rs.{' '}
                          {item.unitPrice.toLocaleString('en-LK')}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-3988">
                        Rs. {item.total.toLocaleString('en-LK')}
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-3989">
                <div className="flex justify-between items-center" data-id="element-3990">
                  <span className="text-lg font-semibold text-gray-900" data-id="element-3991">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-gray-900" data-id="element-3992">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-3993">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-3994">
                Continue to Verification
              </button>
            </div>
          </div>}
        {/* Step 2: Verification */}
        {step === 2 && <div className="space-y-6" data-id="element-3995">
            <div data-id="element-3996">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3997">
                Verification Checklist
              </h2>
              <p className="text-gray-600" data-id="element-3998">
                Please verify the following items before making a decision
              </p>
            </div>
            <div className="space-y-4" data-id="element-3999">
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-4000">
                <input type="checkbox" checked={verificationChecks.supplierVerified} onChange={() => handleCheckboxChange('supplierVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-4001" />
                <div className="ml-3" data-id="element-4002">
                  <div className="text-sm font-medium text-gray-900" data-id="element-4003">
                    Supplier Information Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4004">
                    Confirm that the supplier details are correct and the
                    supplier is authorized
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-4005">
                <input type="checkbox" checked={verificationChecks.amountVerified} onChange={() => handleCheckboxChange('amountVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-4006" />
                <div className="ml-3" data-id="element-4007">
                  <div className="text-sm font-medium text-gray-900" data-id="element-4008">
                    Amount Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4009">
                    Confirm that the total amount matches the purchase order or
                    agreement
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-4010">
                <input type="checkbox" checked={verificationChecks.itemsVerified} onChange={() => handleCheckboxChange('itemsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-4011" />
                <div className="ml-3" data-id="element-4012">
                  <div className="text-sm font-medium text-gray-900" data-id="element-4013">
                    Items and Quantities Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4014">
                    Confirm that all items and quantities are correct
                  </div>
                </div>
              </label>
              <label className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer" data-id="element-4015">
                <input type="checkbox" checked={verificationChecks.termsVerified} onChange={() => handleCheckboxChange('termsVerified')} className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-blue-500" data-id="element-4016" />
                <div className="ml-3" data-id="element-4017">
                  <div className="text-sm font-medium text-gray-900" data-id="element-4018">
                    Payment Terms Verified
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4019">
                    Confirm that the payment terms and due date are acceptable
                  </div>
                </div>
              </label>
            </div>
            {!allChecksCompleted && <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-4020">
                <AlertCircleIcon size={20} className="text-yellow-600 mt-0.5" data-id="element-4021" />
                <div className="text-sm text-yellow-800" data-id="element-4022">
                  Please complete all verification checks before proceeding
                </div>
              </div>}
            <div className="flex justify-between" data-id="element-4023">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4024">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!allChecksCompleted} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4025">
                Continue to Decision
              </button>
            </div>
          </div>}
        {/* Step 3: Decision */}
        {step === 3 && <div className="space-y-6" data-id="element-4026">
            <div data-id="element-4027">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4028">
                Approval Decision
              </h2>
              <p className="text-gray-600" data-id="element-4029">
                Make your final decision on this bill
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-4030">
              <button onClick={() => setApprovalDecision('approve')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'approve' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`} data-id="element-4031">
                <CheckCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'approve' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-4032" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-4033">
                  Approve
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-4034">
                  Approve this bill for payment
                </div>
              </button>
              <button onClick={() => setApprovalDecision('reject')} className={`p-6 border-2 rounded-lg transition-all ${approvalDecision === 'reject' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'}`} data-id="element-4035">
                <XCircleIcon size={48} className={`mx-auto mb-3 ${approvalDecision === 'reject' ? 'text-red-600' : 'text-gray-400'}`} data-id="element-4036" />
                <div className="text-lg font-semibold text-gray-900" data-id="element-4037">
                  Reject
                </div>
                <div className="text-sm text-gray-500 mt-1" data-id="element-4038">
                  Reject this bill and return to supplier
                </div>
              </button>
            </div>
            <div data-id="element-4039">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4040">
                Notes {approvalDecision === 'reject' && '*'}
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder={approvalDecision === 'reject' ? 'Please provide a reason for rejection...' : 'Add any additional notes (optional)...'} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4041" />
            </div>
            <div className="flex justify-between" data-id="element-4042">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4043">
                Back
              </button>
              <button onClick={handleSubmit} disabled={!approvalDecision || approvalDecision === 'reject' && !notes} className={`px-6 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed ${approvalDecision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-4044">
                {approvalDecision === 'approve' ? 'Approve Bill' : 'Reject Bill'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}